import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
// ── FIREBASE AUTHENTICATION IS GONE FROM THIS SCREEN ──
// Registration no longer creates a Firebase Auth account and no longer sends a
// Firebase verification link. The account is created in MongoDB and verified
// with a Resend OTP on /verify-otp. (Firebase itself is still used by the app
// for PUSH NOTIFICATIONS — see services/fcm.js — which this change did not
// touch.)
// SECURITY (10.1): stable client device fingerprint sent with registration.
import { getDeviceFingerprint } from "../utils/deviceFingerprint";
import "./Register.css";
import BannerAds from "./BannerAds";
import { validateReferralCode } from "../services/salesApi";

function Register() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  // One device = one account: the SERVER's refusal, kept on screen (a toast
  // vanishes before most people have read it). Nothing here decides anything.
  const [deviceBlock, setDeviceBlock] = useState("");
  const [refCode, setRefCode] = useState("");
  // "" (nothing typed / unknown) | "checking" | "valid" | "invalid"
  const [refStatus, setRefStatus] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref && ref.trim()) {
      const code = ref.trim();
      setRefCode(code);
      localStorage.setItem("referralCode", code);
    } else {
      const stored = localStorage.getItem("referralCode");
      if (stored) setRefCode(stored);
    }
  }, []);

  // ================================
  // REFERRAL CODE VALIDATION
  // Covers the "no referral link available" path: the user searches
  // TheBlogsSphere, registers normally and types the code by hand. Checking it
  // live means a typo is caught while it can still be fixed, instead of the
  // account silently registering with no referrer attached.
  // A network failure leaves the status unknown ("") and never blocks signup.
  // ================================
  useEffect(() => {
    const code = refCode.trim();
    if (!code) {
      setRefStatus("");
      return;
    }

    let cancelled = false;
    setRefStatus("checking");

    const timer = setTimeout(async () => {
      try {
        const res = await validateReferralCode(code);
        if (!cancelled) setRefStatus(res.valid ? "valid" : "invalid");
      } catch {
        if (!cancelled) setRefStatus("");
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [refCode]);

  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const animRef = useRef(null);
  const COLORS = ["#6332ff", "#00ffc8", "#f472b6", "#60a5fa", "#a78bfa"];

  const makeParticle = useCallback((W, H) => ({
    x: Math.random() * W, y: Math.random() * H, vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
    r: Math.random() * 1.8 + 0.4, color: COLORS[Math.floor(Math.random() * COLORS.length)],
    alpha: Math.random() * 0.6 + 0.2, pulse: Math.random() * Math.PI * 2,
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current; const root = rootRef.current;
    if (!canvas ||!root) return; const ctx = canvas.getContext("2d");
    let W = root.offsetWidth; let H = root.offsetHeight; let particles = [];
    const resize = () => { W = root.offsetWidth; H = root.offsetHeight; canvas.width = W; canvas.height = H; };
    resize(); particles = Array.from({ length: 80 }, () => makeParticle(W, H));
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.018;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0; if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        const pa = p.alpha * (0.7 + 0.3 * Math.sin(p.pulse));
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color; ctx.globalAlpha = pa; ctx.fill();
      });
      ctx.globalAlpha = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x; const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath(); ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = particles[i].color; ctx.lineWidth = 0.5;
            ctx.globalAlpha = (1 - dist / 100) * 0.12; ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1; animRef.current = requestAnimationFrame(draw);
    };
    draw(); const ro = new ResizeObserver(resize); ro.observe(root);
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(animRef.current); ro.disconnect(); window.removeEventListener("resize", resize); };
  }, [makeParticle]);

  const showToast = (message, type = "info") => {
    const toast = document.createElement("div");
    toast.className = `rg-toast rg-toast-${type}`;
    toast.innerText = message;
    document.body.appendChild(toast);
    setTimeout(() => { toast.remove(); }, 3000);
  };

  const register = async (e) => {
    e.preventDefault();
    if (!terms) { showToast("Accept Terms & Conditions", "error"); return; }
    if (password.length < 6) { showToast("Password must be at least 6 characters", "error"); return; }
    // A code that has been confirmed wrong is a typo — stop here rather than
    // create the account with the referral silently dropped.
    if (refCode.trim() && refStatus === "invalid") {
      showToast("Invalid referral code. Fix it or clear the field.", "error");
      return;
    }
    setLoading(true);
    setDeviceBlock("");

    try {
      const BASE_URL = window.location.hostname === "localhost"? "http://localhost:5000" : "https://success-blog-app-backend.onrender.com";

      const normalisedEmail = email.trim().toLowerCase();

      await axios.post(`${BASE_URL}/api/auth/check`, { name: name.trim(), email: normalisedEmail });

      // SECURITY (10.1): compute the device fingerprint and send it alongside
      // the registration. Additive fields — the backend treats them as optional.
      const fp = await getDeviceFingerprint();

      // ── ONE REQUEST CREATES THE ACCOUNT AND SENDS THE CODE ──
      // There is no Firebase account to create first, and therefore no orphan
      // Firebase user to roll back when the backend refuses the device (the
      // CR-01 rollback that used to live here is gone with it). A refused
      // registration now creates nothing anywhere, by construction.
      const res = await axios.post(`${BASE_URL}/api/auth/register`, {
        name: name.trim(), email: normalisedEmail, password,
       ...(refCode? { ref: refCode } : {}),
        deviceHash: fp.deviceHash,
        // CR-01: the persistent device id — a second, independent key so the
        // one-device-one-account block survives the fingerprint drifting.
        deviceId: fp.deviceId,
        clientFingerprint: fp.signals,
      });

      localStorage.removeItem("referralCode");
      setName(""); setEmail(""); setPassword(""); setTerms(false); setRefCode(""); setRefStatus("");

      // ── STRAIGHT TO THE OTP SCREEN (Part 2 step 3, Part 3) ──
      // No "check your email / continue" waiting page any more: the code is
      // already on its way, so the next thing the user needs is the field to
      // type it into. The email travels in router state so the OTP screen knows
      // whose account it is verifying without asking again; `replace` keeps
      // Back from returning to a form that has already been submitted.
      navigate("/verify-otp", {
        replace: true,
        state: {
          email: normalisedEmail,
          otpSent: res.data?.otpSent !== false,
          notice: res.data?.otpMessage || "",
          // Present only when the backend is running with OTP_DEV_ECHO in a
          // non-production environment. Undefined in production.
          devCode: res.data?.devCode,
        },
      });

    } catch (err) {
      console.error(err);
      if (err.response?.data?.field === "device") {
        setDeviceBlock(err.response.data.message);
      }
      showToast(err.response?.data?.message || "Registration failed", "error");
    } finally { setLoading(false); }
  };

  return (
    <div className="rg-root" ref={rootRef}>
      <canvas className="rg-canvas" ref={canvasRef} />
      <div className="rg-aurora" />
      <div className="rg-card">
        <div className="rg-card-scanline" />
        <div className="rg-brand-label">✦ TheBlogsSphere</div>
        <h2 className="rg-title">Create Account 🚀</h2>
        <p className="rg-subtitle">join the community of creators</p>

        {/* REFER & EARN — no referral content on this page. The Referral Code
            field below still captures and validates the code exactly as before;
            the steps are explained on the Refer & Earn page only. */}

        <form onSubmit={register} className="rg-form" autoComplete="off">
          <div className="rg-field-group">
            <label className="rg-label">Your Name</label>
            <input className="rg-input" type="text" name="signup-name" autoComplete="off" placeholder="John Doe" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="rg-field-group">
            <label className="rg-label">Email</label>
            {/* A shared referral link opens this page in a browser that may
                already have saved credentials. This is a NEW account, so the
                browser must not offer someone else's — the field is named off
                the standard "email"/"username" pattern and opts out of
                autofill. The referral code below is filled by the app itself
                and is unaffected. */}
            <input className="rg-input" type="email" name="signup-email" autoComplete="off" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="rg-field-group">
            <label className="rg-label">Password</label>
            <div className="rg-password-wrap">
              {/* "new-password" is the value browsers actually honour on a
                  signup form: it stops a saved password being filled in. */}
              <input className="rg-input" type={showPassword? "text" : "password"} name="signup-password" autoComplete="new-password" placeholder="Min. 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <span className="rg-eye" onClick={() => setShowPassword(!showPassword)}>{showPassword? "🙈" : "👁"}</span>
            </div>
          </div>
          <div className="rg-field-group">
            <label className="rg-label">Referral Code <span className="rg-optional">(optional)</span></label>
            <input
              className="rg-input"
              type="text"
              placeholder="e.g. A1B2C3D4"
              value={refCode}
              onChange={(e) => setRefCode(e.target.value.toUpperCase())}
              autoCapitalize="characters"
              name="signup-refcode"
              autoComplete="off"
            />
            {refCode.trim() && refStatus === "checking" && (
              <small className="rg-ref-hint">Checking code…</small>
            )}
            {refCode.trim() && refStatus === "valid" && (
              <small className="rg-ref-hint rg-ref-ok">✅ Referral code applied</small>
            )}
            {refCode.trim() && refStatus === "invalid" && (
              <small className="rg-ref-hint rg-ref-bad">⚠️ Invalid referral code</small>
            )}
          </div>

          <div className="rg-reminder">🔑 Note : Remember your password. You need it every time to login.</div>
          <div className="rg-terms-row">
            <input className="rg-checkbox" type="checkbox" checked={terms} onChange={() => setTerms(!terms)} id="rg-terms" />
            <label htmlFor="rg-terms" className="rg-terms-label">I agree to the <Link to="/terms" className="rg-link">Terms</Link> &amp; <Link to="/privacy" className="rg-link">Privacy Policy</Link></label>
          </div>
          {deviceBlock && (
            <div className="rg-device-block" role="alert">
              <strong>🚫 Registration not allowed on this device</strong>
              <span>{deviceBlock}</span>
              <span>Already have an account? <Link to="/" className="rg-link">Log in here</Link></span>
            </div>
          )}
          <button className="rg-submit" disabled={loading}>{loading? (<span className="rg-spinner-row"><span className="rg-spinner" />Registering...</span>) : ("Create Account →")}</button>
        </form>
        <div className="rg-divider" />
        <BannerAds />
        <p className="rg-footer-text">Already have an account? <Link to="/" className="rg-link">Login</Link></p>
      </div>
    </div>
  );
}
export default Register;